import { useContext } from 'react'

// Context
import { ShareVersionContext } from '../../../modules/versioning'
import { usePreview } from '../../../modules/shares/views/PreviewView/PreviewContext'

import { useGetPagePresentationFileQuery } from '@sketch/gql-types'
import { getRenderStatus } from '@sketch/modules-common'

interface UseCanvasPresentationFileProps {
  pageUUID: string
}

const SKETCH_MODEL_WITH_FRAMES = 156

export const useCanvasPresentationFile = ({
  pageUUID,
}: UseCanvasPresentationFileProps) => {
  const shareVersionContext = useContext(ShareVersionContext)!
  const previewContext = usePreview()

  const shouldSkipQuery = shareVersionContext?.loading
    ? true
    : shareVersionContext?.currentVersion?.presentationStatus === 'PENDING'

  const prFileQuery = useGetPagePresentationFileQuery({
    variables: {
      shareIdentifier: shareVersionContext?.share.identifier,
      versionShortId: shareVersionContext?.versionShortId,
      pageUUID,
    },
    skip: shouldSkipQuery || !shareVersionContext,
  })

  if (!shareVersionContext) {
    return {
      page: previewContext?.currentPage,
      isLoading: previewContext?.isLoadingPage,
      hasError: previewContext?.hasErrorLoading,
      // things we can't provide on the preview mode
      share: undefined,
      versionShortId: undefined,
      isRendering: false,
      showArtboardDecorations: false,
    }
  }

  const { versionShortId, share, loading } = shareVersionContext

  const renderStatus = !loading
    ? getRenderStatus(
        shareVersionContext.currentVersion.document?.renderStatus ?? null
      )
    : null

  const page = prFileQuery.data?.share?.version?.document?.page
  const isLoading = prFileQuery.loading
  const hasError = prFileQuery.error
  const isRendering = renderStatus !== 'ready'

  const currentDocumentVersion = shareVersionContext.loading
    ? 0
    : shareVersionContext.currentVersion.document?.documentVersion || 0

  const showArtboardDecorations =
    currentDocumentVersion < SKETCH_MODEL_WITH_FRAMES

  return {
    share,
    page,
    versionShortId,
    isLoading,
    hasError,
    isRendering,
    showArtboardDecorations,
  }
}
